import React from 'react';
import PropTypes from 'prop-types';
import { USER_SUBSCRIPTIONS, CAM_TIERS, CELL_SUBSCRIPTIONS, HEAT_STRESS_SUBSCRIPTIONS, isCameraSubscription, isCellularSubscription, isHeatStressSubscription } from './index';
import './SubscriptionDisplayCard.less';

function SubscriptionDisplayCard({ subscription, children }) {
  if (!subscription) {
    return null;
  }

  // Detect subscription type
  const isCameraSub = isCameraSubscription(subscription);
  const isCellSub = isCellularSubscription(subscription);
  const isHeatStressSub = isHeatStressSubscription(subscription);
  // Check for yearly/annual: "yearly", "one-year", "three-year", etc.
  const isYearly = subscription.includes('yearly') || /-year-/.test(subscription);

  let tierName, planName, price, logoClass, tierClass;

  if (isCameraSub) {
    // Camera subscription handling
    const isSilver = /tier1/.test(subscription);
    const isGold = /tier2/.test(subscription);

    tierName = isSilver ? 'Silver' : isGold ? 'Gold' : 'Standard';
    planName = `Camera ${tierName}`;
    logoClass = isSilver ? 'camera-logo silver' : isGold ? 'camera-logo gold' : 'camera-logo';
    tierClass = isSilver ? 'tier-name silver' : isGold ? 'tier-name gold' : 'tier-name';

    // Find price from CAM_TIERS
    const camConfig = CAM_TIERS.find(tier => tier.productName === subscription);
    if (camConfig) {
      // Use displayPrice if available, otherwise format the price
      price = camConfig.displayPrice || `$${camConfig.price}`;
    } else {
      price = '';
    }
  } else if (isCellSub) {
    // Cellular subscription handling
    tierName = 'Cellular';
    planName = 'KestrelMet Cellular Plan';
    logoClass = 'cellular-logo';
    tierClass = 'tier-name';

    // Find price from CELL_SUBSCRIPTIONS
    const cellConfig = CELL_SUBSCRIPTIONS.find(sub => sub.productName === subscription);
    if (cellConfig) {
      price = `$${cellConfig.price}/year`;
    } else {
      price = '';
    }
  } else if (isHeatStressSub) {
    // Heat Stress subscription handling
    const isFree = subscription === 'heat-stress-free';

    if (isFree) {
      // Free tier handling
      tierName = 'Free';
      planName = 'Heat Stress Free';
      logoClass = 'heat-stress-logo';
      tierClass = 'tier-name heat-stress';
      price = '';
    } else {
      // Paid tiers
      const isStandard = /standard/.test(subscription);
      const isPro = /pro/.test(subscription);
      const isEnterprise = /enterprise/.test(subscription);

      tierName = isStandard ? 'Heat Stress Standard' : isPro ? 'Heat Stress Pro' : isEnterprise ? 'Heat Stress Enterprise' : '';
      planName = `AWN ${tierName}`;
      logoClass = 'heat-stress-logo';
      tierClass = 'tier-name heat-stress';

      // Find price from HEAT_STRESS_SUBSCRIPTIONS
      const heatStressConfig = HEAT_STRESS_SUBSCRIPTIONS.find(sub => sub.productName === subscription);
      if (heatStressConfig) {
        price = heatStressConfig.displayPrice || `$${heatStressConfig.price}/month`;
      } else {
        price = '';
      }
    }
  } else {
    // AWN+ subscription handling
    const isTeams = subscription.includes('for-teams');

    tierName = isTeams ? 'Teams' : 'Individual';
    planName = isTeams ? 'AWN+ Teams' : 'AWN+ Individual';
    logoClass = 'awn-logo';
    tierClass = 'tier-name';

    // Find the subscription config to get the price
    const subscriptionConfig = USER_SUBSCRIPTIONS.find(
      sub => sub.productName === subscription
    );
    price = subscriptionConfig ? subscriptionConfig.price : '';
  }

  const term = isYearly ? 'Annual' : 'Monthly';
  const showTerm = !isHeatStressSub || subscription !== 'heat-stress-free';

  return (
    <div className="payment-subscription-display-card">
      <div className="logo-section">
        <div className={logoClass}></div>
        {!isHeatStressSub && <div className={tierClass}>{tierName}</div>}
      </div>
      <div className="details-section">
        <h3 className="plan-name">{planName}</h3>
        {showTerm && <div className="term">{term}</div>}
        {price && <div className="price">{price}</div>}
        {children}
      </div>
    </div>
  );
}

SubscriptionDisplayCard.propTypes = {
  subscription: PropTypes.string,
  children: PropTypes.node
};

SubscriptionDisplayCard.defaultProps = {
  subscription: null,
  children: null
};

export default SubscriptionDisplayCard;

SubscriptionDisplayCard.displayName = 'SubscriptionDisplayCard'
